import PropTypes from "prop-types";
import Dispatcher from "Dispatcher";
import ContextedReduceStore from "./util/ContextedReduceStore";
import typeCheckingStore from "./util/typeCheckingStore";
import SuperRecordActions from "./SuperRecordActions";
import RecordActions, {
    OPT_USER_CONFIRMED,
    OPT_USER_INITIATED,
} from "./RecordActions";
import ClientId from "data/ClientId";
import { recordIdType } from "data/util/typedefs";

const hasEdits = state =>
    state.deletion || state.records.length > 0;

const recordEdited = (state, key, id) => {
    const compoundId = key + "#" + id;
    if (state.records.some(id => id === compoundId)) {
        // already recorded as dirty
        return state;
    }
    return {
        ...state,
        records: state.records.concat(compoundId),
    };
};

const recordDeleted = (state, key, id) => {
    const compoundId = key + "#" + id;
    return {
        ...state,
        deletion: true,
        records: state.records.filter(id => id !== compoundId),
    };
};

export class UserEditedRecordStore extends ContextedReduceStore {

    getInitialContext() {
        return {
            deletion: false,
            records: [],
        };
    }

    hasEdits(ctx) {
        if(ctx) {
            return hasEdits(this.getState(ctx));
        }
        return this.getEachContext().some(hasEdits);
    }

    getRecordKeys(ctx) {
        const keySet = new Set();
        if (ctx) {
            this.getState(ctx).records.forEach(k => keySet.add(k));
        } else {
            this.getEachContext().forEach(s => s.records.forEach(k => keySet.add(k)));
        }
        return Array.from(keySet)
            .map(k => k.split("#"))
            .map(([key, id]) => ({
                key,
                id: ClientId.is(id)
                    ? id
                    : parseInt(id)
            }));
    }

    reduceContext(ctx, action) {
        switch (action.type) {

            case SuperRecordActions.NEW: {
                return {
                    ...this.getInitialContext(),
                    key: action.stereoKey,
                    id: action.newId,
                };
            }

            case SuperRecordActions.OPEN: {
                return {
                    ...this.getInitialContext(),
                    key: action.key,
                    id: action.id,
                };
            }

            case RecordActions.NEW: {
                return recordEdited(ctx, action.recordKey, action.newId);
            }

            case RecordActions.NEW_CHILD: {
                return recordEdited(ctx, action.recordKey, action.recordId);
            }

            case RecordActions.DELETE_CHILD: {
                if (!action[OPT_USER_CONFIRMED]) return ctx;
                ctx = recordEdited(ctx, action.recordKey, action.recordId);
                ctx = recordDeleted(ctx, action.childKey, action.childId);
                return ctx;
            }

            case RecordActions.DELETE_RECORD: {
                if (!action[OPT_USER_CONFIRMED]) return ctx;
                return recordDeleted(ctx, action.recordKey, action.recordId);
            }

            case RecordActions.SET_FIELD: {
                if (!action[OPT_USER_INITIATED]) return ctx;
                return recordEdited(ctx, action.recordKey, action.recordId);
            }

            default:
                return ctx;
        }
    }

    reduceEachContext(ctx, action) {
        switch (action.type) {
            case SuperRecordActions.CREATED:
            case SuperRecordActions.UPDATED: {
                if (ctx.key !== action.key || ctx.id !== action.id) return ctx;
                return {
                    ...this.getInitialContext(),
                    key: action.key,
                    id: action.data.id,
                };
            }

            case SuperRecordActions.DELETED: {
                if (ctx.key !== action.key || ctx.id !== action.id) return ctx;
                return this.getInitialContext();
            }

            default:
                return ctx;
        }
    }

}

UserEditedRecordStore.contextTypes = {
    key: PropTypes.string,
    id: recordIdType,
    deletion: PropTypes.bool.isRequired,
    records: PropTypes.arrayOf(
        PropTypes.string
    ).isRequired
};

export default typeCheckingStore(new UserEditedRecordStore(Dispatcher));



// WEBPACK FOOTER //
// ./app/data/userEditedRecordStore.js